import { useState } from "react";
import { useToast } from "../../../hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useRenameProject } from "../hooks/use-create-project";
import { deleteProjectById } from "../services/projects-services";
import { useChatStore } from "../../../state/store/chat/use-chat-store";
import { useConfig } from "../../../hooks/use-config";

export function useProjectActions(projectId: string) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { configurationSettings } = useConfig();
    const { mutateAsync: renameProject } = useRenameProject();
    const navigate = useNavigate();
    const { projectId: currentProjectId, conversationId } = useParams();

    const updateProjectNameInStore = useChatStore((s) => s.updateProjectNameInStore);
    const setSidebarProjectsList = useChatStore((s) => s.setSidebarProjectsList);
    const sidebarProjectsList = useChatStore((s) => s.sidebarProjectsList);

    const [isRenameLoading, setIsRenameLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const rename = async (newTitle: string) => {
        if (!newTitle.trim() || !projectId) return;
        try {
            setIsRenameLoading(true);
            await renameProject({ payload: { projectId, name: newTitle } });
            updateProjectNameInStore(projectId, newTitle);

            toast({ variant: "success", title: t("SUCCESS"), description: t("PROJECT_RENAME_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("RENAME_FAILED"), description: t("PROJECT_RENAME_FAILED_DESCRIPTION") });
        } finally {
            setIsRenameLoading(false);
        }
    };

    const remove = async () => {
        if (!projectId) return;
        try {
            setIsDeleteLoading(true);
            await deleteProjectById(configurationSettings, projectId);

            setSidebarProjectsList(sidebarProjectsList.filter((p) => p.id !== projectId));

            const project = sidebarProjectsList.find((p) => p.id === projectId);
            if (conversationId && project?.items.some((item) => item.ConversationId === conversationId)) {
                useChatStore.getState().setNewChat(true);
                navigate("/chat");
            }
            if (projectId === currentProjectId) {
                useChatStore.getState().setNewChat(true);
                navigate("/chat");
            }
            toast({ variant: "success", title: t("SUCCESS"), description: t("PROJECT_DELETE_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("DELETE_FAILED"), description: t("PROJECT_DELETE_FAILED_DESCRIPTION") });
        } finally {
            setIsDeleteLoading(false);
        }
    };

    return {
        rename,
        remove,
        isRenameLoading,
        isDeleteLoading,
    };
}
